import React, { useEffect, useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import api from "@/lib/api";
import { Search, FileText, Download } from "lucide-react";

const CAT_ORDER = { "Bylaws": 0, "Collective Agreement": 1, "CUPE Constitution": 2 };

export default function Resources() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const cats = t("resources.categories");

  useEffect(() => {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (cat !== "all") params.append("category", cat);
    api.get(`/resources?${params.toString()}`).then((r) => {
      const sorted = [...r.data].sort(
        (a, b) => (CAT_ORDER[a.category] ?? 99) - (CAT_ORDER[b.category] ?? 99),
      );
      setItems(sorted);
    }).catch(() => {});
  }, [q, cat]);

  return (
    <div data-testid="resources-page">
      <section className="bg-gradient-to-br from-pink-50 via-white to-rose-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900">{t("resources.title")}</h1>
          <p className="text-lg text-slate-600 mt-3 max-w-2xl">{t("resources.sub")}</p>
          <div className="h-1 w-20 cupe-gradient rounded-full mt-4" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("resources.searchPlaceholder")}
              className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-md focus:border-[#d81b7a] focus:ring-2 focus:ring-pink-200 outline-none text-base min-h-[48px]"
              data-testid="resources-search"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {Array.isArray(cats) && cats.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`px-4 py-2 rounded-full text-sm font-semibold ${cat === c ? "cupe-gradient text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-[#d81b7a]"}`}
                data-testid={`res-cat-${c}`}
              >
                {c === "all" ? t("common.all") : c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {items.map((r) => (
            <a key={r.id} href={r.file_url} target="_blank" rel="noreferrer" download className="bg-white border border-slate-200 rounded-xl p-5 flex items-start gap-4 hover:border-[#d81b7a] hover:shadow-md transition" data-testid="resource-item">
              <div className="w-12 h-12 rounded-lg cupe-gradient flex items-center justify-center flex-shrink-0"><FileText className="text-white" size={22} /></div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-[#d81b7a] font-semibold">{r.category}</div>
                <div className="font-display font-semibold text-slate-900 mt-1">{lang === "fr" ? r.title_fr : r.title_en}</div>
                {(r.description_en || r.description_fr) && <div className="text-sm text-slate-500 mt-1">{lang === "fr" ? r.description_fr : r.description_en}</div>}
              </div>
              <Download size={20} className="text-[#d81b7a] flex-shrink-0" />
            </a>
          ))}
          {items.length === 0 && <p className="text-slate-400 col-span-2">{t("resources.noResults")}</p>}
        </div>
      </section>
    </div>
  );
}
